/**
 * @fileoverview Help Settings Page
 *
 * Purpose: In-app entry point to the Morgan Help & Documentation hub
 *          (hosted at docs.morgan.so) and the Morgan team's direct contact
 *          details.
 * URL: /dashboard/settings/help
 * Protection: Requires authentication
 */

import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, LifeBuoy, Mail, Phone, Info } from "lucide-react";
import { Button } from "@/ui/primitives";

const HELP_DOCS_URL = "https://docs.morgan.so";

const TEAM_CONTACTS = [
  {
    name: "Josh Abulafia",
    email: "josh@morgan.so",
    phone: "0401 528 335",
    phoneHref: "+61401528335",
  },
  {
    name: "Elle Curran",
    email: "elle@morgan.so",
    phone: "0410 573 756",
    phoneHref: "+61410573756",
  },
] as const;

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/help",
)({
  component: HelpSettingsPage,
  beforeLoad: () => ({
    title: "Help",
    headerTitle: "Help & Support",
    headerDescription: "Documentation and direct contacts for the Morgan team.",
  }),
});

function HelpSettingsPage() {
  return (
    <div className="flex w-full flex-col gap-6">
      <HelpDocsCard />
      <ContactCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Help Docs card
// ---------------------------------------------------------------------------

function HelpDocsCard() {
  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <LifeBuoy className="size-5" />
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              Morgan Help Docs
            </h2>
            <p className="text-sm font-normal text-primary/60">
              Step-by-step guides, FAQs and reference material covering every
              part of Morgan. Updated as the product evolves.
            </p>
          </div>
        </div>
        <Button
          asChild
          size="sm"
          className="shrink-0 gap-1.5 self-start sm:self-center"
        >
          <a href={HELP_DOCS_URL} target="_blank" rel="noopener noreferrer">
            Open Help Docs
            <ExternalLink className="size-3.5" />
          </a>
        </Button>
      </div>
      <div className="flex min-h-14 w-full items-start gap-2 border-t border-border/50 bg-secondary/50 px-6 py-3 text-sm text-primary/60">
        <Info className="mt-0.5 size-3.5 shrink-0 text-primary/40" />
        <p>
          Can&apos;t find what you&apos;re looking for? Reach out to the Morgan
          team using the details below and we&apos;ll add it to the docs.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact card
// ---------------------------------------------------------------------------

function ContactCard() {
  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-2 p-6 pb-2">
        <h2 className="text-xl font-medium text-primary">Contact the team</h2>
        <p className="text-sm font-normal text-primary/60">
          Reach out directly with questions, feedback or anything else.
        </p>
      </div>
      <div className="grid gap-4 p-6 pt-4 sm:grid-cols-2">
        {TEAM_CONTACTS.map((contact) => (
          <ContactRow key={contact.email} contact={contact} />
        ))}
      </div>
    </div>
  );
}

function ContactRow({ contact }: { contact: (typeof TEAM_CONTACTS)[number] }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-secondary/30 p-4">
      <p className="text-sm font-medium text-primary">{contact.name}</p>
      <div className="flex flex-col gap-2">
        <a
          href={`mailto:${contact.email}`}
          className="flex items-center gap-2 text-sm text-primary/70 transition-colors hover:text-primary"
        >
          <Mail className="size-3.5 shrink-0 text-primary/40" />
          <span className="truncate">{contact.email}</span>
        </a>
        <a
          href={`tel:${contact.phoneHref}`}
          className="flex items-center gap-2 text-sm text-primary/70 transition-colors hover:text-primary"
        >
          <Phone className="size-3.5 shrink-0 text-primary/40" />
          <span>{contact.phone}</span>
        </a>
      </div>
    </div>
  );
}
